import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  FaChartPie, FaClipboardList, FaTruck, FaFileAlt,
  FaCog, FaUser, FaSignOutAlt, FaEnvelope, FaBell,
  FaSearch, FaQuestionCircle, FaPlusCircle,
  FaCheckCircle, FaFileInvoice, FaChartLine,
  FaComment, FaHistory, FaMoneyBillWave, FaGavel,
  FaBox, FaEye, FaMapMarkerAlt, FaLifeRing, FaFolderOpen, FaUsers
} from 'react-icons/fa';
import { API_URL } from '../config';
import NotificationBell from '../components/NotificationBell';
import MessagesDropdown from '../components/MessagesDropdown';
import ClientProfileDropdown from '../components/ClientProfileDropdown';
import SupportModal from '../components/SupportModal';

function ClientLayout({ children, activeTab, onLogout, user }) {
  const currentUser = user || JSON.parse(localStorage.getItem('user') || '{}');
  // PHASE 5 — a "projects only" login sees just the project area (nav items are
  // hidden by the .portal-projects-only CSS rules; the route guard blocks URLs).
  const projectsOnly = currentUser?.portal_scope === 'projects_only';
  const [companyName, setCompanyName] = useState('');
  const [supportOpen, setSupportOpen] = useState(false);

  useEffect(() => {
    const fetchClient = async () => {
      try {
        const response = await fetch(`${API_URL}/clients?user_id=${currentUser.id}`);
        const data = await response.json();
        if (data.success && data.clients && data.clients.length > 0) {
          setCompanyName(data.clients[0].company_name || 'Client');
        }
      } catch (err) {
        console.error('Error fetching client:', err);
      }
    };
    if (currentUser.id) {
      fetchClient();
    }
  }, [currentUser.id]);

  const getActiveBg = (tab) => {
    return activeTab === tab ? '#00A86B' : 'transparent';
  };

  const getActiveColor = (tab) => {
    return activeTab === tab ? '#0A1929' : '#94A3B8';
  };

  return (
    <div className="flex min-h-screen" style={{ backgroundColor: '#F8F9FA' }}>
      {/* Sidebar */}
      <div className="w-64 text-white fixed h-full overflow-y-auto" style={{ backgroundColor: '#0A1929' }}>
        <div className="p-6 border-b" style={{ borderColor: '#1E2F3F' }}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center font-bold text-lg" style={{ backgroundColor: '#00A86B', color: '#0A1929' }}>
              <span className="text-sm">OV</span>
            </div>
            <div className="font-bold text-lg">
              ONE<span style={{ color: '#00A86B' }}>VIEW</span>
            </div>
          </div>
          <p className="text-xs mt-1" style={{ color: '#94A3B8' }}>Powered by One Purpose Logistics</p>
          {companyName && (
            <p className="text-xs mt-1 font-medium" style={{ color: '#00A86B' }}>{companyName}</p>
          )}
          {projectsOnly && (
            <p className="text-[10px] mt-2 inline-block px-2 py-0.5 rounded-full" style={{ backgroundColor: 'rgba(0,168,107,0.15)', color: '#00A86B' }}>
              Projects access
            </p>
          )}
        </div>

        <nav className={`p-4${projectsOnly ? ' portal-projects-only' : ''}`}>
          <p className="text-xs uppercase tracking-wider px-3 mb-3" style={{ color: '#94A3B8' }}>Main</p>
          
          <Link 
            to="/client-dashboard"
            className={`flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors cursor-pointer`}
            style={{ 
              backgroundColor: getActiveBg('dashboard'),
              color: getActiveColor('dashboard')
            }}
          >
            <FaChartPie size={18} /> Dashboard
          </Link>
          
          <Link 
            to="/client-new-job"
            className={`flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors cursor-pointer`}
            style={{ 
              backgroundColor: getActiveBg('newjob'),
              color: getActiveColor('newjob')
            }}
          >
            <FaPlusCircle size={18} /> New Job
          </Link>

          <Link 
            to="/client-quotes"
            className={`flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors cursor-pointer`}
            style={{ 
              backgroundColor: getActiveBg('quotes'),
              color: getActiveColor('quotes')
            }}
          >
            <FaMoneyBillWave size={18} /> My Quotes
          </Link>
          
          <Link
            to="/client-projects"
            className={`flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors cursor-pointer`}
            style={{
              backgroundColor: getActiveBg('projects'),
              color: getActiveColor('projects')
            }}
          >
            <FaFolderOpen size={18} /> Projects
          </Link>

          <Link 
            to="/client-jobs"
            className={`flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors cursor-pointer`}
            style={{ 
              backgroundColor: getActiveBg('jobs'),
              color: getActiveColor('jobs')
            }}
          >
            <FaClipboardList size={18} /> My Jobs
          </Link>

          {/* My Loads — a Link (not a button), so it keeps the SPA alive */}
          <Link
            to="/client-loads"
            className={`flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors cursor-pointer`}
            style={{
              backgroundColor: getActiveBg('loads'),
              color: getActiveColor('loads')
            }}
          >
            <FaBox size={18} /> My Loads
          </Link>
          
          <Link 
            to="/client-live-map"
            className={`flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors cursor-pointer`}
            style={{ 
              backgroundColor: getActiveBg('livemap'),
              color: getActiveColor('livemap')
            }}
          >
            <FaMapMarkerAlt size={18} /> Live Map
          </Link>
          
          <Link 
            to="/client-completed"
            className={`flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors cursor-pointer`}
            style={{ 
              backgroundColor: getActiveBg('completed'),
              color: getActiveColor('completed')
            }}
          >
            <FaCheckCircle size={18} /> Completed Jobs
          </Link>

          <Link 
            to="/client-invoices"
            className={`flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors cursor-pointer`}
            style={{ 
              backgroundColor: getActiveBg('invoices'),
              color: getActiveColor('invoices')
            }}
          >
            <FaFileInvoice size={18} /> Invoices
          </Link>

          <Link 
            to="/client-reports"
            className={`flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors cursor-pointer`}
            style={{ 
              backgroundColor: getActiveBg('reports'),
              color: getActiveColor('reports')
            }}
          >
            <FaChartLine size={18} /> Reports
          </Link>

          <div className="border-t my-3" style={{ borderColor: '#1E2F3F' }}></div>
          
          <p className="text-xs uppercase tracking-wider px-3 mb-3" style={{ color: '#94A3B8' }}>Management</p>
          
          <Link 
            to="/client-team"
            className={`flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors cursor-pointer`}
            style={{ 
              backgroundColor: getActiveBg('team'),
              color: getActiveColor('team')
            }}
          >
            <FaUsers size={18} /> Team
          </Link>

          <Link 
            to="/client-profile"
            className={`flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors cursor-pointer`}
            style={{ 
              backgroundColor: getActiveBg('profile'),
              color: getActiveColor('profile')
            }}
          >
            <FaUser size={18} /> Profile
          </Link>
          
          <Link 
            to="/client-settings"
            className={`flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors cursor-pointer`}
            style={{ 
              backgroundColor: getActiveBg('settings'),
              color: getActiveColor('settings')
            }}
          >
            <FaCog size={18} /> Settings
          </Link>

          <div className="border-t my-3" style={{ borderColor: '#1E2F3F' }}></div>
          
          <p className="text-xs uppercase tracking-wider px-3 mb-3" style={{ color: '#94A3B8' }}>Support</p>
          
          <Link 
            to="/client-help"
            className={`flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors cursor-pointer`}
            style={{ 
              backgroundColor: getActiveBg('help'),
              color: getActiveColor('help')
            }}
          >
            <FaQuestionCircle size={18} /> Help Center
          </Link>

          <Link 
            to="/client-feedback"
            className={`flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors cursor-pointer`}
            style={{ 
              backgroundColor: getActiveBg('feedback'),
              color: getActiveColor('feedback')
            }}
          >
            <FaComment size={18} /> Feedback
          </Link>

          <button
            onClick={() => setSupportOpen(true)}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors cursor-pointer text-left"
            style={{ color: '#94A3B8' }}
            onMouseEnter={(e) => e.currentTarget.style.color = '#00A86B'}
            onMouseLeave={(e) => e.currentTarget.style.color = '#94A3B8'}
          >
            <FaLifeRing size={18} /> Contact Support
          </button>

          <div className="border-t my-3" style={{ borderColor: '#1E2F3F' }}></div>
          
          <button 
            onClick={onLogout}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors cursor-pointer"
            style={{ color: '#94A3B8' }}
            onMouseEnter={(e) => e.currentTarget.style.color = '#EF4444'}
            onMouseLeave={(e) => e.currentTarget.style.color = '#94A3B8'}
          >
            <FaSignOutAlt size={18} /> Logout
          </button>
        </nav>
      </div>

      {/* Main Content */}
      <div className="ml-64 flex-1 min-w-0 max-w-full">
        {/* Top Bar */}
        <div className="border-b px-8 py-4 flex justify-between items-center sticky top-0 z-10" style={{ backgroundColor: '#FFFFFF', borderColor: '#E8ECEF' }}>
          <div className="flex items-center gap-3">
            <span className="text-sm" style={{ color: '#4A5568' }}>{new Date().toLocaleDateString()}</span>
          </div>
          <div className="flex items-center gap-3">
            <MessagesDropdown userId={currentUser?.id} />
            <NotificationBell userId={currentUser?.id} />
            <ClientProfileDropdown user={currentUser} onLogout={onLogout} />
          </div>
        </div>

        {/* Page Content */}
        <div className="p-8">
          {children}
        </div>
      </div>
      <SupportModal isOpen={supportOpen} onClose={() => setSupportOpen(false)} />
    </div>
  );
}

export default ClientLayout;